import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";

// Components
import Sidebar from "./components/Sidebar";
import Login from "./auth/Login";
import Dashboard from "./components/Dashboard";
import ForgotPassword from "./auth/ForgotPassword";
import ResetPassword from "./auth/ResetPassword";

// Auth utilities
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { getValidToken, clearAllStorage, getUserData } from "./auth/authUtils";
import { setupTokenRefresh, validateTokens } from "./utils/tokenRefresh";

// Debug utilities
import { debugLog, debugInfo, isDebugMode } from "./utils/debug";

// Hooks
import { useUserSettings } from "./hooks/useUserSettings";

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Логирование изменений user для отладки
  useEffect(() => {
    console.log("🔍 [App] User state changed:", user);
  }, [user]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        debugInfo("App", "Checking authentication status...");
        
        // Сначала проверяем валидность токенов
        const tokensValid = validateTokens();
        if (!tokensValid) {
          console.log("🔒 Tokens invalid or missing - clearing auth");
          clearAllStorage();
          setUser(null);
          setIsLoading(false);
          return;
        }
        
        const token = await getValidToken();

        if (token) {
          // User is authenticated - загружаем данные пользователя
          const userData = getUserData();
          
          console.log("🔍 [App] Token found, userData from localStorage:", userData);
          
          if (userData && userData.email) {
            setUser(userData);
            debugLog("App", "User authenticated with valid token:", userData.email);
          } else {
            // Если нет данных пользователя, но есть токен - устанавливаем минимальные данные
            setUser({ authenticated: true });
            debugLog("App", "User authenticated but no user data found");
          }
          
          // Восстанавливаем автоматическое обновление токена
          // Токен живет 60 минут по умолчанию
          const tokenExpiry = localStorage.getItem("tokenExpiry");
          if (tokenExpiry) {
            const now = new Date().getTime();
            const expiresIn = Math.max(0, Math.floor((parseInt(tokenExpiry) - now) / 1000));
            if (expiresIn > 0) {
              setupTokenRefresh(expiresIn);
              debugLog("App", `Token refresh scheduled for ${expiresIn}s`);
            } else {
              console.log("🔒 Token expired - clearing auth");
              clearAllStorage();
              setUser(null);
            }
          }
        } else {
          // User is not authenticated
          setUser(null);
          debugLog("App", "No valid token found");
        }
      } catch (error) {
        debugLog("App", "Auth check failed:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleLogin = (userData) => {
    console.log("🔐 [App] handleLogin called with userData:", userData);
    
    // Проверяем, нужно ли обновлять состояние
    if (!user || user.email !== userData.email) {
      setUser(userData);
      debugLog("App", "User logged in successfully");
    } else {
      console.log("⚠️ [App] User already set, skipping setState");
    }
  };

  const handleLogout = () => {
    clearAllStorage();
    setUser(null);
    debugLog("App", "User logged out");
  };

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider value={{ user, login: handleLogin, logout: handleLogout }}>
      <div className="App">
        <Router>
          {user ? (
            <Routes>
              <Route path="/" element={<Dashboard onLogout={handleLogout} />} />
              <Route path="/dashboard" element={
                <div className="app-container">
                  <Sidebar onLogout={handleLogout} />
                  <main className="main-content">
                    <Dashboard />
                  </main>
                </div>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          )}
        </Router>
      </div>
    </AuthProvider>
  );
};

export default App;
